import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  ChevronLeft,
  Edit2,
  CreditCard,
  Building,
  User,
  Phone,
  Mail,
  Building2,
  Navigation,
  Map as MapIcon,
  Globe,
  MapPin,
  Flag,
  ChevronRight,
  Star,
  Award,
  Landmark,
  Loader2,
  AlertTriangle
} from 'lucide-react';
import { vendorApi } from '../services/api';
import { useTheme } from '../context/ThemeContext';

function B2BPartnerDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { currentTheme } = useTheme();

  const [partner, setPartner] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      setError(null);
      try {
        const v = await vendorApi.getVendor(id);
        if (cancelled) return;
        setPartner({
          companyName: v.companyName || '',
          contactPerson: v.contactPersonName || '',
          contactNumber: v.contactNumber || '',
          email: v.email || '',
          panNumber: v.panNumber || '',
          fssaiNumber: v.fssaiNumber || '',
          drugLicense1: v.drugLicensePart1 || '',
          drugLicense2: v.drugLicensePart2 || '',
          gstin: v.gstin || '',
          buildingNumber: v.buildingNumber || '',
          blockName: v.blockRoadName || '',
          areaName: v.areaName || '',
          state: v.state || '',
          pincode: v.areaPinCode || '',
          city: v.cityTown || '',
          district: v.district || '',
          outstandingBills: v.purchasedBillsTotal ?? 0,
          orders: v.ordersCount ?? 0,
          id: v.id,
        });
      } catch (err) {
        setError(err.message || 'Failed to load vendor');
        console.error('Vendor load error:', err);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [id]);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#F8FAFC]">
        <div className="flex items-center gap-3 text-gray-600">
          <Loader2 className="w-5 h-5 animate-spin" />
          <span className="font-semibold">Loading vendor...</span>
        </div>
      </div>
    );
  }

  if (error || !partner) {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center p-6 bg-[#F8FAFC]">
        {error && (
          <div className="flex items-center gap-2 text-rose-600 mb-4 text-sm font-semibold">
            <AlertTriangle className="w-4 h-4" />
            <span>{error}</span>
          </div>
        )}
        <h3 className="text-xl font-bold uppercase tracking-widest mb-4 text-gray-800">Vendor not found</h3>
        <button
          onClick={() => navigate('/b2b-partners')}
          className="px-8 py-3 rounded-2xl text-white font-semibold uppercase tracking-widest text-xs transition-all active:scale-95"
          style={{ backgroundColor: currentTheme.color }}
        >
          Return to directory
        </button>
      </div>
    );
  }

  return (
    <div className="min-h-screen p-6 bg-[#F8FAFC] space-y-6">

      {/* Title & Back Button */}
      <div className="flex items-center gap-3">
        <button
          onClick={() => navigate('/b2b-partners')}
          className="p-1.5 rounded-full border border-emerald-500/30 text-emerald-600 hover:bg-emerald-50 transition-all flex items-center justify-center shadow-sm"
        >
          <ChevronLeft className="w-5 h-5" />
        </button>
        <h1 className="text-2xl font-bold text-gray-800">{partner.companyName}</h1>
      </div>

      {/* Row 1: Document details, Drug licence details, GSTIN */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">

        {/* Document Details Card */}
        <div className="space-y-2">
          <p className="text-sm font-semibold text-gray-500">Documents details</p>
          <div className="bg-white rounded-2xl p-4 border border-[#CCFBF1] shadow-sm grid grid-cols-2 gap-4 h-[76px] items-center">
            {/* PAN */}
            <div className="flex items-center gap-3">
              <div className="w-9 h-9 rounded-lg bg-blue-50 flex items-center justify-center text-blue-500 flex-shrink-0">
                <CreditCard className="w-5 h-5" />
              </div>
              <div className="min-w-0 text-left">
                <p className="text-[10px] text-gray-400 font-semibold">Pan number</p>
                <p className="text-xs font-bold text-gray-800 truncate mt-0.5">{partner.panNumber}</p>
              </div>
            </div>
            {/* FSSAI */}
            <div className="flex items-center gap-3">
              <div className="w-9 h-9 rounded-lg bg-amber-50 flex items-center justify-center text-amber-500 flex-shrink-0">
                <Star className="w-5 h-5 fill-amber-500/10" />
              </div>
              <div className="min-w-0 text-left">
                <p className="text-[10px] text-gray-400 font-semibold">FSSAI number</p>
                <p className="text-xs font-bold text-gray-800 truncate mt-0.5">{partner.fssaiNumber}</p>
              </div>
            </div>
          </div>
        </div>

        {/* Drug Licence Details Card */}
        <div className="space-y-2">
          <p className="text-sm font-semibold text-gray-500">Drug licence details</p>
          <div className="bg-white rounded-2xl p-4 border border-[#CCFBF1] shadow-sm grid grid-cols-2 gap-4 h-[76px] items-center">
            {/* Part 1 */}
            <div className="flex items-center gap-3">
              <div className="w-9 h-9 rounded-lg bg-emerald-50 flex items-center justify-center text-emerald-500 flex-shrink-0">
                <Award className="w-5 h-5" />
              </div>
              <div className="min-w-0 text-left">
                <p className="text-[10px] text-gray-400 font-semibold">Part 1</p>
                <p className="text-xs font-bold text-gray-800 truncate mt-0.5">{partner.drugLicense1}</p>
              </div>
            </div>
            {/* Part 2 */}
            <div className="flex items-center gap-3">
              <div className="w-9 h-9 rounded-lg bg-emerald-50 flex items-center justify-center text-emerald-500 flex-shrink-0">
                <Award className="w-5 h-5" />
              </div>
              <div className="min-w-0 text-left">
                <p className="text-[10px] text-gray-400 font-semibold">Part 2</p>
                <p className="text-xs font-bold text-gray-800 truncate mt-0.5">{partner.drugLicense2}</p>
              </div>
            </div>
          </div>
        </div>

        {/* GSTIN Card */}
        <div className="space-y-2">
          <p className="text-sm font-semibold text-gray-500">GSTIN</p>
          <div className="bg-white rounded-2xl p-4 border border-[#CCFBF1] shadow-sm flex items-center gap-3 h-[76px]">
            <div className="w-9 h-9 rounded-lg bg-gray-50 border border-gray-150 flex items-center justify-center text-gray-500 flex-shrink-0">
              <Building className="w-5 h-5" />
            </div>
            <div className="min-w-0 text-left">
              <p className="text-[10px] text-gray-400 font-semibold">GSTIN</p>
              <p className="text-xs font-bold text-gray-700 truncate mt-0.5">{partner.gstin}</p>
            </div>
          </div>
        </div>

      </div>

      {/* Row 2: Company details & Vertical stats */}
      <div className="grid grid-cols-1 lg:grid-cols-4 gap-6">

        {/* Company Details Card */}
        <div className="lg:col-span-3 bg-white rounded-2xl border border-gray-200/80 p-6 relative shadow-sm flex flex-col justify-between min-h-[170px]">
          {/* Edit Button */}
          <button
            onClick={() => navigate(`/b2b-partners/${partner.id}/edit`)}
            className="absolute top-4 right-4 p-1.5 bg-amber-50 text-amber-500 hover:bg-amber-100 rounded-lg border border-amber-200 transition-all active:scale-95 shadow-sm"
          >
            <Edit2 className="w-3.5 h-3.5" />
          </button>

          <div>
            <h3 className="text-sm font-bold text-gray-800 mb-6 text-left">Company details</h3>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              {/* Company Name */}
              <div className="flex items-start gap-3">
                <div className="w-9 h-9 rounded-lg bg-emerald-50 flex items-center justify-center text-emerald-500 flex-shrink-0">
                  <Building className="w-5 h-5" />
                </div>
                <div className="min-w-0 text-left">
                  <p className="text-[10px] text-gray-400 font-semibold">Company name</p>
                  <p className="text-xs font-bold text-gray-800 truncate mt-0.5">{partner.companyName}</p>
                </div>
              </div>
              {/* Contact Person Name */}
              <div className="flex items-start gap-3">
                <div className="w-9 h-9 rounded-lg bg-blue-50 flex items-center justify-center text-blue-500 flex-shrink-0">
                  <User className="w-5 h-5" />
                </div>
                <div className="min-w-0 text-left">
                  <p className="text-[10px] text-gray-400 font-semibold">Contact person name</p>
                  <p className="text-xs font-bold text-gray-800 truncate mt-0.5">{partner.contactPerson}</p>
                </div>
              </div>
              {/* Contact Number */}
              <div className="flex items-start gap-3">
                <div className="w-9 h-9 rounded-lg bg-emerald-50 flex items-center justify-center text-emerald-500 flex-shrink-0">
                  <Phone className="w-5 h-5" />
                </div>
                <div className="min-w-0 text-left">
                  <p className="text-[10px] text-gray-400 font-semibold">Contact number</p>
                  <p className="text-xs font-bold text-gray-800 truncate mt-0.5">{partner.contactNumber}</p>
                </div>
              </div>
            </div>
          </div>

          {/* Email Row */}
          <div className="mt-4 bg-[#F0FDFA] rounded-xl p-3 border border-emerald-100/50 inline-flex items-center gap-3 w-fit">
            <div className="w-8 h-8 rounded-lg bg-emerald-50 flex items-center justify-center text-emerald-600 flex-shrink-0">
              <Mail className="w-4 h-4" />
            </div>
            <div className="min-w-0 pr-2 text-left">
              <p className="text-[9px] text-gray-400 font-semibold">Email</p>
              <p className="text-xs font-bold text-emerald-800 truncate mt-0.5">{partner.email || '—'}</p>
            </div>
          </div>
        </div>

        {/* Right side stats stack */}
        <div className="flex flex-col gap-4 justify-between h-full">
          {/* Purchased Bills Card */}
          <div className="bg-[#F59E0B] text-white p-5 rounded-2xl shadow-sm flex flex-col justify-between h-[100px] text-left">
            <p className="text-[11px] font-semibold opacity-90">Purchased bills:</p>
            <p className="text-3xl font-bold">₹ {partner.outstandingBills.toLocaleString()}</p>
          </div>
          {/* Orders Card */}
          <div className="bg-[#10B981] text-white p-5 rounded-2xl shadow-sm flex flex-col justify-between h-[100px] relative text-left">
            <div>
              <p className="text-[11px] font-semibold opacity-90">Orders</p>
              <p className="text-3xl font-bold">{partner.orders}</p>
            </div>
            <div
              onClick={() => navigate(`/b2b-partners/${partner.id}/orders`)}
              className="absolute bottom-4 right-4 w-7 h-7 bg-white text-[#10B981] rounded-full flex items-center justify-center cursor-pointer transition-all hover:bg-gray-50 shadow-sm"
            >
              <ChevronRight className="w-4 h-4 pointer-events-none" />
            </div>
          </div>
        </div>

      </div>

      {/* Row 3: Billing Address Card */}
      <div className="bg-white rounded-2xl border border-gray-200/80 p-6 relative shadow-sm">
        {/* Edit Button */}
        <button
          onClick={() => navigate(`/b2b-partners/${partner.id}/edit`)}
          className="absolute top-4 right-4 p-1.5 bg-amber-50 text-amber-500 hover:bg-amber-100 rounded-lg border border-amber-200 transition-all active:scale-95 shadow-sm"
        >
          <Edit2 className="w-3.5 h-3.5" />
        </button>

        <h3 className="text-sm font-bold text-gray-800 mb-6 text-left">Billing Address</h3>

        {/* Billing Address Details Grid */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
          {/* Building Number */}
          <div className="flex items-start gap-3">
            <div className="w-9 h-9 rounded-lg bg-purple-50 flex items-center justify-center text-purple-500 flex-shrink-0">
              <Building2 className="w-5 h-5" />
            </div>
            <div className="min-w-0 text-left">
              <p className="text-[10px] text-gray-400 font-semibold">Building Number</p>
              <p className="text-xs font-bold text-gray-800 mt-0.5">{partner.buildingNumber}</p>
            </div>
          </div>
          {/* Block/Road Name */}
          <div className="flex items-start gap-3">
            <div className="w-9 h-9 rounded-lg bg-emerald-50 flex items-center justify-center text-emerald-500 flex-shrink-0">
              <Navigation className="w-5 h-5" />
            </div>
            <div className="min-w-0 text-left">
              <p className="text-[10px] text-gray-400 font-semibold">Block/Road Name</p>
              <p className="text-xs font-bold text-gray-800 mt-0.5">{partner.blockName}</p>
            </div>
          </div>
          {/* Area Name */}
          <div className="flex items-start gap-3">
            <div className="w-9 h-9 rounded-lg bg-gray-50 flex items-center justify-center text-gray-500 flex-shrink-0">
              <MapIcon className="w-5 h-5" />
            </div>
            <div className="min-w-0 text-left">
              <p className="text-[10px] text-gray-400 font-semibold">Area Name</p>
              <p className="text-xs font-bold text-gray-800 mt-0.5">{partner.areaName}</p>
            </div>
          </div>
          {/* State (Top Row) */}
          <div className="flex items-start gap-3">
            <div className="w-9 h-9 rounded-lg bg-amber-50 flex items-center justify-center text-amber-500 flex-shrink-0">
              <Globe className="w-5 h-5" />
            </div>
            <div className="min-w-0 text-left">
              <p className="text-[10px] text-gray-400 font-semibold">State</p>
              <p className="text-xs font-bold text-gray-800 mt-0.5">{partner.state}</p>
            </div>
          </div>
          {/* Area Pin Code */}
          <div className="flex items-start gap-3">
            <div className="w-9 h-9 rounded-lg bg-amber-50 flex items-center justify-center text-amber-500 flex-shrink-0">
              <MapPin className="w-5 h-5" />
            </div>
            <div className="min-w-0 text-left">
              <p className="text-[10px] text-gray-400 font-semibold">Area Pin code</p>
              <p className="text-xs font-bold text-gray-800 mt-0.5">{partner.pincode}</p>
            </div>
          </div>
          {/* City/Town */}
          <div className="flex items-start gap-3">
            <div className="w-9 h-9 rounded-lg bg-emerald-50 flex items-center justify-center text-emerald-500 flex-shrink-0">
              <Flag className="w-5 h-5" />
            </div>
            <div className="min-w-0 text-left">
              <p className="text-[10px] text-gray-400 font-semibold">City/Town</p>
              <p className="text-xs font-bold text-gray-800 mt-0.5">{partner.city}</p>
            </div>
          </div>
          {/* District */}
          <div className="flex items-start gap-3">
            <div className="w-9 h-9 rounded-lg bg-gray-50 flex items-center justify-center text-gray-500 flex-shrink-0">
              <MapIcon className="w-5 h-5" />
            </div>
            <div className="min-w-0 text-left">
              <p className="text-[10px] text-gray-400 font-semibold">District</p>
              <p className="text-xs font-bold text-gray-800 mt-0.5">{partner.district}</p>
            </div>
          </div>
          {/* State (Bottom Row) */}
          <div className="flex items-start gap-3">
            <div className="w-9 h-9 rounded-lg bg-red-50 flex items-center justify-center text-red-500 flex-shrink-0">
              <Landmark className="w-5 h-5" />
            </div>
            <div className="min-w-0 text-left">
              <p className="text-[10px] text-gray-400 font-semibold">State</p>
              <p className="text-xs font-bold text-gray-800 mt-0.5">{partner.state}</p>
            </div>
          </div>
        </div>
      </div>

    </div>
  );
}

export default B2BPartnerDetail;